// Price history — "what have we quoted this before, and did we win with it?"
//
// Two surfaces, one data source: the inline <LastQuoted> note that sits under a
// rate cell while you price, and <PriceHistoryModal> behind its 📈 button.
// The Price Book page (pages/PriceBook.jsx) uses the same endpoints.
//
// DISPLAY RULE that matters: an equipment rate is meaningless without its
// billing basis and tier — the fleet's MX6 reads $22-$55, which is a $22
// Utilization line at tier 50 and a $55 Non-Consignment line at tier 100, not
// one item priced two ways. Every equipment row prints both.

import { useEffect, useState } from 'react';
import { api } from '../api';
import { styles, colors, fmtDate } from '../components/UI';

const ORANGE = colors?.orange || '#E36F2C';

export const money = (v) => (v == null || v === '' || Number.isNaN(Number(v)))
  ? '—' : `$${Number(v).toFixed(2)}`;

// Won / lost / open — the outcome chip.
const OUTCOME = {
  won:       { label: 'WON',   bg: '#D1FAE5', fg: '#065F46', bd: '#6EE7B7' },
  lost:      { label: 'LOST',  bg: '#FEE2E2', fg: '#B91C1C', bd: '#FCA5A5' },
  open:      { label: 'OPEN',  bg: '#FEF3C7', fg: '#92400E', bd: '#FCD34D' },
  abandoned: { label: 'CLOSED',bg: '#F3F4F6', fg: '#6B7280', bd: '#E5E7EB' },
  unknown:   { label: '—',     bg: '#F3F4F6', fg: '#9CA3AF', bd: '#E5E7EB' },
};

export const OutcomeChip = ({ outcome, title }) => {
  const o = OUTCOME[outcome] || OUTCOME.unknown;
  return (
    <span title={title || `Opportunity outcome: ${o.label}`}
      style={{ background: o.bg, color: o.fg, border: `1px solid ${o.bd}`, borderRadius: 4,
        fontSize: 9, fontWeight: 800, letterSpacing: 0.4, padding: '1px 5px', whiteSpace: 'nowrap' }}>
      {o.label}
    </span>
  );
};

// ---- Inline hint ------------------------------------------------------------
// One line under a rate cell. Leads with what WON, because that is the number
// being asked for; falls back to the last quote when nothing has been decided.
export const LastQuoted = ({ hint, onOpen }) => {
  if (!hint) return null;
  const mine = hint.customer;
  const fleet = hint.fleet;
  const wonBand = (mine?.won?.won_count ? mine.won : fleet.won);
  const scope = (mine?.won?.won_count ? 'them' : 'fleet');

  return (
    <div style={{ fontSize: 10, color: '#6B7280', marginTop: 2, lineHeight: 1.45 }}>
      {mine ? (
        <span style={{ color: '#065F46', fontWeight: 700 }}>
          {money(mine.last.rate)} to {mine.last.customer_name}
          <span style={{ fontWeight: 400, color: '#6B7280' }}> ({mine.last.estimate_label})</span>
        </span>
      ) : (
        <span>no history with this customer</span>
      )}
      {wonBand?.won_count > 0 && (
        <span style={{ marginLeft: 6 }}>
          · won <strong style={{ color: '#065F46' }}>
            {wonBand.min === wonBand.max ? money(wonBand.min) : `${money(wonBand.min)}–${money(wonBand.max)}`}
          </strong>
          <span style={{ color: '#9CA3AF' }}> ({wonBand.won_count}/{wonBand.decided_count} {scope})</span>
        </span>
      )}
      {!wonBand?.won_count && fleet.count > 0 && (
        <span style={{ marginLeft: 6 }}>
          · fleet {money(fleet.min)}–{money(fleet.max)} <span style={{ color: '#9CA3AF' }}>(none decided yet)</span>
        </span>
      )}
      <button type="button" onClick={onOpen}
        title="Open the full price history for this item"
        style={{ marginLeft: 6, background: 'transparent', border: 'none', color: ORANGE,
          cursor: 'pointer', fontSize: 10, fontWeight: 700, padding: 0 }}>
        📈 history
      </button>
    </div>
  );
};

// ---- Full history modal -----------------------------------------------------
export const PriceHistoryModal = ({ kind, itemKey, customerId, label, onClose }) => {
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    let live = true;
    api.priceHistoryDetail({ kind, key: itemKey, customer_id: customerId })
      .then(d => { if (live) setData(d); })
      .catch(e => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [kind, itemKey, customerId]);

  const Band = ({ title, block, tone }) => {
    if (!block || !block.count) return null;
    return (
      <div style={{ flex: '1 1 190px', border: `1px solid ${tone.bd}`, background: tone.bg,
        borderRadius: 6, padding: '8px 10px' }}>
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 0.4, color: tone.fg }}>{title}</div>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#1F2937', marginTop: 2 }}>
          {block.min === block.max ? money(block.min) : `${money(block.min)} – ${money(block.max)}`}
        </div>
        <div style={{ fontSize: 10, color: '#6B7280', marginTop: 1 }}>
          median {money(block.median)} · {block.count} quote{block.count === 1 ? '' : 's'}
          {block.win_rate != null && <> · won {block.won_count}/{block.decided_count} ({block.win_rate}%)</>}
        </div>
      </div>
    );
  };

  const rows = data?.rows || [];

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 8,
        maxWidth: 940, width: '100%', maxHeight: '86vh', overflowY: 'auto', padding: 18 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
          <div>
            <div style={{ fontSize: 10, color: '#6B7280', fontWeight: 700, letterSpacing: 0.4 }}>
              PRICE HISTORY · {String(kind).toUpperCase()}
            </div>
            <div style={{ fontSize: 17, fontWeight: 800, color: '#1F2937' }}>{data?.label || label || itemKey}</div>
          </div>
          <button onClick={onClose} style={{ ...styles.btnSecondary, fontSize: 12 }}>✕ Close</button>
        </div>

        {err && <div style={{ color: '#B91C1C', fontSize: 12 }}>{err}</div>}
        {!data && !err && <div style={{ color: '#6B7280', fontSize: 12 }}>Loading…</div>}

        {data && (
          <>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
              <Band title="WON AT" block={data.fleet.won}
                tone={{ bg: '#ECFDF5', bd: '#6EE7B7', fg: '#065F46' }} />
              <Band title="THIS CUSTOMER" block={data.customer}
                tone={{ bg: '#FFF7ED', bd: '#FDBA74', fg: '#9A3412' }} />
              <Band title="ALL QUOTES" block={data.fleet}
                tone={{ bg: '#F9FAFB', bd: '#E5E7EB', fg: '#374151' }} />
            </div>

            {!rows.length && (
              <div style={{ fontSize: 12, color: '#6B7280' }}>
                Never priced before — this is the first time.
              </div>
            )}

            {rows.length > 0 && (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr style={{ background: '#1F2937', color: '#fff' }}>
                    <th style={th}>Rate</th>
                    {kind === 'labor' && <th style={th}>OT</th>}
                    {kind === 'labor' && <th style={th}>Per diem</th>}
                    {kind === 'equipment' && <th style={th}>Basis</th>}
                    {kind === 'equipment' && <th style={th}>Tier</th>}
                    {kind === 'consumable' && <th style={th}>Unit</th>}
                    <th style={th}>Outcome</th>
                    <th style={th}>Customer</th>
                    <th style={th}>Job</th>
                    <th style={th}>Estimate</th>
                    <th style={th}>Quoted</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={`${r.estimate_id}-${r.line_id}-${i}`}
                      style={{ borderBottom: '1px solid #F3F4F6',
                        background: r.customer_id && customerId && r.customer_id === customerId ? '#FFF7ED' : undefined }}>
                      <td style={{ ...td, fontWeight: 800, color: '#1F2937' }}>{money(r.rate)}</td>
                      {kind === 'labor' && <td style={td}>{money(r.bill_ot)}</td>}
                      {kind === 'labor' && <td style={td}>{money(r.per_diem_bill)}</td>}
                      {kind === 'equipment' && <td style={td}>{r.equip_type}{r.tp_bill_basis ? ` (${r.tp_bill_basis})` : ''}</td>}
                      {kind === 'equipment' && <td style={td}>{r.utilization_tier}%</td>}
                      {kind === 'consumable' && <td style={td}>{r.unit || '—'}</td>}
                      <td style={td}>
                        <OutcomeChip outcome={r.outcome}
                          title={`Opportunity status: ${r.project_status || 'unknown'}${r.po_number ? ` · PO ${r.po_number}` : ''}${r.quote_accepted ? ' · quote accepted' : ''}`} />
                      </td>
                      <td style={td}>{r.customer_name || '—'}</td>
                      <td style={td}>{r.project_site || '—'}</td>
                      <td style={td}>
                        {r.estimate_label}
                        <span style={{ color: '#9CA3AF' }}> · {r.estimate_status}</span>
                        {r.is_change_order && <span style={{ color: '#5B21B6', fontWeight: 700 }}> · CO</span>}
                        {r.went_to_customer && <span title="A quote carrying this rate was sent to the customer"> 📤</span>}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(r.quoted_at) || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {data.similar?.length > 0 && (
              <div style={{ marginTop: 12, fontSize: 11, color: '#6B7280' }}>
                <strong style={{ color: '#374151' }}>Similar wording, priced separately</strong> — not counted
                above, because merging on a guess would put someone else's rate under this heading:
                <ul style={{ margin: '4px 0 0 16px' }}>
                  {data.similar.map(s => (
                    <li key={s.key}>{s.label} — {s.count} quote{s.count === 1 ? '' : 's'}, {money(s.min)}–{money(s.max)}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

const th = { textAlign: 'left', padding: '5px 7px', fontSize: 10, fontWeight: 700, whiteSpace: 'nowrap' };
const td = { padding: '4px 7px', color: '#374151' };

// ---- Hint loader ------------------------------------------------------------
// ONE request per estimate, not one per line.
// `nonce` refetches when lines are added or removed — a brand-new line has no
// hint until the map is rebuilt, and stale hints would point at deleted ids.
export const usePriceHints = (estimateId, nonce = 0) => {
  const [hints, setHints] = useState(null);
  useEffect(() => {
    if (!estimateId) { setHints(null); return; }
    let live = true;
    api.priceHintsForEstimate(estimateId)
      .then(h => { if (live) setHints(h); })
      .catch(() => { if (live) setHints(null); });   // advisory only — never break the estimate
    return () => { live = false; };
  }, [estimateId, nonce]);
  return hints;
};
